import { forwardRef, useState, useEffect } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ColorSpectrum } from './ColorSpectrum';
import { HueBar } from './HueBar';
import { hslToHex, hexToHsl, isPartialHex, isValidHex } from './utils';
import { FormField } from '../FormField';
import { Button } from 'components/ui/Button';

interface ColorPickerProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  required?: boolean;
}

export const ColorPicker = forwardRef<HTMLInputElement, ColorPickerProps>(
  ({ label, error, required, className, value, onChange, disabled, ...props }, ref) => {
    const [currentColor, setCurrentColor] = useState(value as string || '#000000');
    const [initialColor, setInitialColor] = useState(value as string || '#000000');
    const [hue, setHue] = useState(0);
    const [saturation, setSaturation] = useState(100);
    const [lightness, setLightness] = useState(50);

    // Initialiser les valeurs HSL à partir de la valeur initiale
    useEffect(() => {
      if (value) {
        setCurrentColor(value as string);
        const hsl = hexToHsl(value as string);
        setHue(hsl.h);
        setSaturation(hsl.s);
        setLightness(hsl.l);

        if (!initialColor || initialColor === '#000000') {
          setInitialColor(value as string);
        }
      }
    }, [value]);

    // Mettre à jour la couleur et notifier le changement
    const updateColor = (h: number, s: number, l: number) => {
      const hexColor = hslToHex(h, s, l);
      setCurrentColor(hexColor);
      setHue(h);
      setSaturation(s);
      setLightness(l);

      const syntheticEvent = {
        target: { name: props.name, value: hexColor },
        currentTarget: { name: props.name, value: hexColor }
      } as React.ChangeEvent<HTMLInputElement>;

      onChange?.(syntheticEvent);
    };

    // Gestion du changement de saturation/luminosité
    const handleSpectrumChange = (newSaturation: number, newLightness: number) => {
      updateColor(hue, newSaturation, newLightness);
    };

    // Gestion du changement de teinte
    const handleHueChange = (newHue: number) => {
      updateColor(newHue, saturation, lightness);
    };

    // Réinitialiser à la couleur initiale
    const handleReset = () => {
      const hsl = hexToHsl(initialColor);
      updateColor(hsl.h, hsl.s, hsl.l);
    };

    // Gestion de l'input hexadécimal
    const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      
      if (isPartialHex(value)) {
        setCurrentColor(value);
        
        if (isValidHex(value)) {
          const hsl = hexToHsl(value);
          setHue(hsl.h);
          setSaturation(hsl.s);
          setLightness(hsl.l);
          
          const syntheticEvent = {
            target: { name: props.name, value },
            currentTarget: { name: props.name, value }
          } as React.ChangeEvent<HTMLInputElement>;

          onChange?.(syntheticEvent);
        }
      }
    };

    return (
      <FormField
        label={label}
        error={error}
        required={required}
        name={props.name || ''}
      >
        <Popover className="relative">
          {({ close }) => (
            <>
              {/* Mêmes gabarits que les autres champs (Dropdown/InputText) :
                  h-12, rounded-lg, bordure simple, fond blanc. */}
              <PopoverButton
                disabled={disabled}
                className="relative w-full h-12 rounded-lg border border-gray-300 bg-white cursor-pointer hover:border-primary transition-colors flex items-center px-4 gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div
                  className="w-6 h-6 rounded-md border border-gray-200 flex-shrink-0"
                  style={{ backgroundColor: currentColor }}
                />
                <span className="text-base sm:text-sm/6 font-medium text-gray-700">
                  {currentColor.toUpperCase()}
                </span>
              </PopoverButton>

              {/* anchor : panel rendu en portal (échappe aux overflow-hidden parents)
                  et retourné automatiquement vers le haut si la place manque en bas —
                  même mécanique que le Dropdown. z-[100] au-dessus des overlays. */}
              <PopoverPanel
                anchor={{ to: 'bottom start', gap: 8, padding: 16 }}
                className="z-[100] p-4 bg-white rounded-lg shadow-xl border border-gray-200 w-80 min-w-[320px]"
              >
                <div className="space-y-4">
                  {/* Aperçu couleur actuelle et bouton reset */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-gray-300 shadow-sm"
                        style={{ backgroundColor: currentColor }}
                      />
                      <div className="text-sm">
                        <div className="font-medium text-gray-700">Couleur sélectionnée</div>
                        <div className="text-gray-500 font-mono">{currentColor.toUpperCase()}</div>
                      </div>
                    </div>

                    {initialColor !== currentColor && (
                      <button
                        type="button"
                        onClick={handleReset}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        <div
                          className="w-6 h-6 rounded border border-gray-300"
                          style={{ backgroundColor: initialColor }}
                        />
                        <span>Réinitialiser</span>
                      </button>
                    )}
                  </div>

                  {/* Nuancier de saturation/luminosité */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Nuancier
                    </div>
                    <ColorSpectrum
                      hue={hue}
                      saturation={saturation}
                      lightness={lightness}
                      onChange={handleSpectrumChange}
                    />
                  </div>

                  {/* Barre de teinte */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Teinte
                    </div>
                    <HueBar hue={hue} onChange={handleHueChange} />
                  </div>

                  {/* Input hexadécimal */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Code hexadécimal
                    </div>
                    <input
                      type="text"
                      value={currentColor}
                      onChange={handleHexInputChange}
                      placeholder="#000000"
                      maxLength={7}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                    />
                  </div>

                  {/* Bouton de validation */}
                  <div className="flex justify-end pt-2 border-t border-gray-200">
                    <Button
                      onClick={() => close()}
                      color="primary"
                    >
                      Valider
                    </Button>
                  </div>
                </div>
              </PopoverPanel>

              {/* Input hidden pour Formik */}
              <input
                ref={ref}
                type="hidden"
                value={currentColor}
                {...props}
              />
            </>
          )}
        </Popover>
      </FormField>
    );
  }
);

ColorPicker.displayName = 'ColorPicker';
